/**
 * Seedance 参考素材时长探测与浏览器内裁剪。
 *
 * Kinovi 约束：参考视频时长总和 ≤15s，参考音频时长总和 ≤15s（各自独立）。
 * - probeDuration / probeBlobDuration：通过媒体元素 metadata 读取时长
 *   （跨域 URL 读 metadata 不需要 CORS）
 * - trimAudioFile：AudioContext 解码 → 切片 → MediaRecorder 编码
 *   （audio/mp4 优先、audio/webm 次之、WAV 兜底）
 * - trimVideoFile：播放捕获（captureStream + MediaRecorder）重编码，
 *   mp4 优先（Chrome 126+/Safari），否则 webm；音轨经 AudioContext
 *   静默路由，裁剪过程无外放声音
 */

export const TYPE_DURATION_LIMIT = 15; // 秒，视频/音频各自总和上限

// 上传素材单文件大小上限（Kinovi 约束）：视频 ≤50MB，图片/音频 ≤20MB。
// 请求生成 API 前必须满足；视频可裁剪后再判（裁短会重编码、体积随之变小）。
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
export const MAX_IMAGE_AUDIO_BYTES = 20 * 1024 * 1024;
export function assetSizeLimit(format: string): number {
  return format === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_AUDIO_BYTES;
}
export const bytesToMB = (n: number) => Math.round((n / (1024 * 1024)) * 10) / 10;

const durationCache = new Map<string, number>();

/**
 * 强制走浏览器下载（fetch → blob → a[download]）。
 * 跨域 URL 直接挂 download 属性会被浏览器忽略而变成页面跳转；
 * 转成 blob 对象后 download 生效。CORS 拒绝时退回新标签页打开。
 */
export async function downloadMediaFile(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // 延迟回收，确保下载已接管
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  } catch {
    window.open(url, '_blank');
  }
}

function probeFromSrc(src: string, format: 'video' | 'audio'): Promise<number> {
  return new Promise((resolve, reject) => {
    const el = document.createElement(format === 'video' ? 'video' : 'audio');
    el.preload = 'metadata';
    const cleanup = () => {
      el.removeAttribute('src');
      el.load();
    };
    el.onloadedmetadata = () => {
      const d = el.duration;
      cleanup();
      if (Number.isFinite(d)) resolve(d);
      else reject(new Error('Duration unavailable'));
    };
    el.onerror = () => {
      cleanup();
      reject(new Error('Failed to load media metadata'));
    };
    el.src = src;
  });
}

/** URL 素材时长（秒），带缓存；探测失败返回 0（不阻塞用户，交给后端兜底） */
export async function probeDuration(url: string, format: 'video' | 'audio'): Promise<number> {
  const cached = durationCache.get(url);
  if (cached !== undefined) return cached;
  try {
    const d = await probeFromSrc(url, format);
    durationCache.set(url, d);
    return d;
  } catch {
    durationCache.set(url, 0);
    return 0;
  }
}

/** 本地文件时长（秒）；探测失败返回 0 */
export async function probeBlobDuration(blob: Blob, format: 'video' | 'audio'): Promise<number> {
  const url = URL.createObjectURL(blob);
  try {
    return await probeFromSrc(url, format);
  } catch {
    return 0;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** 一组素材 URL 的时长总和 */
export async function sumDurations(urls: string[], format: 'video' | 'audio'): Promise<number> {
  const list = await Promise.all(urls.map((u) => probeDuration(u, format)));
  return list.reduce((s, d) => s + d, 0);
}

/* ---------- 音频裁剪：decode → slice → 编码 ---------- */

function encodeWav(channels: Float32Array[], sampleRate: number): Blob {
  const numCh = channels.length;
  const numFrames = channels[0]?.length ?? 0;
  const bytesPerSample = 2;
  const blockAlign = numCh * bytesPerSample;
  const dataSize = numFrames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let ch = 0; ch < numCh; ch++) {
      const v = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(offset, v < 0 ? v * 0x8000 : v * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * 音频裁剪。输出格式按 Kinovi 生产实证选择：
 * m4a（audio/mp4）> opus（audio/webm）> WAV 兜底。
 * 前两种走 MediaRecorder 实时录制（≤15s，报进度），WAV 走即时编码。
 */
export async function trimAudioFile(
  blob: Blob,
  start: number,
  end: number,
  baseName: string,
  onProgress?: (ratio: number) => void
): Promise<File> {
  const arrayBuffer = await blob.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    const sr = decoded.sampleRate;
    const s = Math.max(0, Math.floor(start * sr));
    const e = Math.min(decoded.length, Math.floor(end * sr));
    const numCh = decoded.numberOfChannels;
    const stem = baseName.replace(/\.[^.]+$/, '');

    const mime = MediaRecorder.isTypeSupported('audio/mp4')
      ? 'audio/mp4'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : null;

    if (!mime) {
      // 兜底：即时编码 WAV
      const channels: Float32Array[] = [];
      for (let ch = 0; ch < numCh; ch++) {
        channels.push(decoded.getChannelData(ch).slice(s, e));
      }
      const wav = encodeWav(channels, sr);
      return new File([wav], `${stem}-trimmed.wav`, { type: 'audio/wav' });
    }

    // 切片装进新 AudioBuffer，静默实时播放并录制
    const clip = ctx.createBuffer(numCh, e - s, sr);
    for (let ch = 0; ch < numCh; ch++) {
      clip.copyToChannel(decoded.getChannelData(ch).slice(s, e), ch);
    }
    const source = ctx.createBufferSource();
    source.buffer = clip;
    const dest = ctx.createMediaStreamDestination();
    source.connect(dest); // 不连 ctx.destination → 无外放
    const recorder = new MediaRecorder(dest.stream, { mimeType: mime });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunks.push(ev.data);
    };
    const done = new Promise<Blob>((resolve, reject) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
      recorder.onerror = () => reject(new Error('Recording failed'));
    });
    const clipSecs = (e - s) / sr;
    const startedAt = performance.now();
    const progressTimer = onProgress
      ? window.setInterval(() => {
          onProgress(Math.min(1, (performance.now() - startedAt) / 1000 / clipSecs));
        }, 200)
      : null;
    recorder.start(250);
    source.start();
    await new Promise<void>((resolve) => {
      source.onended = () => resolve();
    });
    recorder.stop();
    const out = await done;
    if (progressTimer !== null) window.clearInterval(progressTimer);
    const ext = mime === 'audio/mp4' ? 'm4a' : 'webm';
    return new File([out], `${stem}-trimmed.${ext}`, { type: mime });
  } finally {
    await ctx.close().catch(() => {});
  }
}

/* ---------- 视频裁剪：播放捕获重编码 ---------- */

export async function trimVideoFile(
  blob: Blob,
  start: number,
  end: number,
  baseName: string,
  onProgress?: (ratio: number) => void
): Promise<File> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.src = url;
  video.playsInline = true;
  const actx = new AudioContext();
  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Failed to load video'));
    });
    video.currentTime = start;
    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
    });

    // 音轨走 AudioContext 静默路由（不连 destination → 不外放，
    // 但 dest.stream 里有完整音频；直接 muted 会导致捕获到静音）
    const srcNode = actx.createMediaElementSource(video);
    const dest = actx.createMediaStreamDestination();
    srcNode.connect(dest);
    const captured = (
      video as HTMLVideoElement & { captureStream: () => MediaStream }
    ).captureStream();
    const stream = new MediaStream([
      ...captured.getVideoTracks(),
      ...dest.stream.getAudioTracks(),
    ]);

    const mime = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm';
    const recorder = new MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: 8_000_000,
    });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    const done = new Promise<Blob>((resolve, reject) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
      recorder.onerror = () => reject(new Error('Recording failed'));
    });

    recorder.start(250);
    await video.play();
    await new Promise<void>((resolve) => {
      const tick = () => {
        const ratio = Math.min(1, (video.currentTime - start) / Math.max(0.1, end - start));
        onProgress?.(ratio);
        if (video.currentTime >= end || video.ended) {
          resolve();
        } else {
          requestAnimationFrame(tick);
        }
      };
      tick();
    });
    video.pause();
    recorder.stop();
    const out = await done;
    const ext = mime === 'video/mp4' ? 'mp4' : 'webm';
    return new File([out], `${baseName.replace(/\.[^.]+$/, '')}-trimmed.${ext}`, {
      type: mime,
    });
  } finally {
    URL.revokeObjectURL(url);
    await actx.close().catch(() => {});
  }
}
